/**
 * **Drone auras** (`ll1-drones` D2/D3, `decisions/0201`): the live-aura query and the static-facet
 * reads. An aura is the {@link AuraProfile} a deployed drone carries, a Burst around the drone's
 * live space, so it moves with the drone and ends when the drone is unplaced or Destroyed.
 *
 * Membership is RAW Hive's "at least partially within the affected area": any cell of the
 * character's footprint inside the disk. Sides are the drone's own (`sideOf`), which `deploy_object`
 * stamps from the deployer.
 */
import { hasStatus, isCharacter, sideOf, worldFor, type AuraFacet, type AuraProfile, type Cell, type Combatant, type EncounterState } from "@lancer-vtt/domain";
import { footprintCells } from "./effects/spatialAnswerer";

/** One projecting aura: the drone and its profile. */
export type LiveAura = { drone: Combatant; profile: AuraProfile };

/** `drone`'s aura, when it projects one now: it carries a profile, is placed and is not Destroyed. */
export function liveAuraOf(drone: Combatant | undefined): LiveAura | undefined {
  const profile = drone?.auraProfile;
  if (drone === undefined || profile === undefined || drone.pos == null || hasStatus(drone.statuses, "Destroyed")) return undefined;
  return { drone, profile };
}

/** Every aura on the board now — placed, not Destroyed drones with a profile — in combatant-id order. */
export function liveAuras(state: EncounterState): LiveAura[] {
  return Object.keys(state.combatants)
    .sort()
    .flatMap((id) => liveAuraOf(state.combatants[id]) ?? []);
}

/** Whether `c`'s footprint is at least partially inside `aura`'s field. An unplaced `c` is in none. */
export function inAura(state: EncounterState, aura: LiveAura, c: Combatant): boolean {
  const world = worldFor(state);
  const field = world.disk(aura.drone.pos!, aura.profile.radius);
  return footprintCells(world, c).some((cell) => field.contains(cell));
}

/**
 * The characters a field acts on: every placed, not Destroyed character other than the drone
 * itself. Side is the authored clause's to check (`hostile_to`), since RAW words each payload for
 * its own side.
 */
export function auraSubject(aura: LiveAura, c: Combatant): boolean {
  return c.id !== aura.drone.id && isCharacter(c) && c.pos != null && !hasStatus(c.statuses, "Destroyed");
}

/**
 * `auras` grouped by deploying entry id (`deployedFrom.entryId`), groups in first-drone order and
 * drones in id order within each. At a given moment only one field per entry id applies to a
 * character ("does not stack", `decisions/0201`), so the fire site walks each group until one
 * fires. A drone with no `deployedFrom` has no entry to fire and is left out.
 */
export function groupAurasByEntry(auras: readonly LiveAura[]): LiveAura[][] {
  const groups = new Map<string, LiveAura[]>();
  for (const aura of auras) {
    const entryId = aura.drone.deployedFrom?.entryId;
    if (entryId === undefined) continue;
    groups.set(entryId, [...(groups.get(entryId) ?? []), aura]);
  }
  return [...groups.values()];
}

/**
 * Walk `auras` by entry ({@link groupAurasByEntry}), calling `fire` on each group's drones in id
 * order until one returns true: at one moment only one field per entry id applies.
 */
export function fireAuraGroups(auras: readonly LiveAura[], fire: (droneId: string) => boolean): void {
  for (const group of groupAurasByEntry(auras)) {
    for (const { drone } of group) if (fire(drone.id)) break;
  }
}

/** The live fields holding `c` now, its own excluded. */
export function aurasHolding(state: EncounterState, c: Combatant): LiveAura[] {
  return liveAuras(state).filter((aura) => auraSubject(aura, c) && inAura(state, aura, c));
}

/**
 * The anchors a move walked, origin first: `path` when the move carries one, otherwise `from` and
 * where `c` stands now. Empty when there is nothing to walk. Shared by drone-aura entry and a mine
 * that fires when a character moves over it (`MineProfile.reach: "over"`).
 */
export function moveWalk(c: Combatant, args: { from: Cell | undefined; path: readonly Cell[] | undefined }): readonly Cell[] {
  const { from, path } = args;
  return path !== undefined && path.length > 1 ? path : from !== undefined && c.pos != null ? [from, c.pos] : [];
}

/** Whether a `walk` ({@link moveWalk}) steps from a cell where `holds` is false into one where it is true. */
export function walkEnters(walk: readonly Cell[], holds: (at: Cell) => boolean): boolean {
  return walk.some((cell, i) => i > 0 && !holds(walk[i - 1]!) && holds(cell));
}

/**
 * The live fields `c` moved into: along `path` (anchor cells, origin first) when the move carries
 * one, so a hostile crossing a field and ending outside it still entered; otherwise between `from`
 * and where `c` stands now. Leaving and re-entering one field is one entry (the round cap is the
 * fire site's). A field that already held `c` at the start and never let go is not entered.
 */
export function aurasEntered(state: EncounterState, c: Combatant, args: { from: Cell | undefined; path: readonly Cell[] | undefined }): LiveAura[] {
  const walk = moveWalk(c, args);
  if (walk.length < 2) return [];
  const inAt = (aura: LiveAura, at: Cell) => inAura(state, aura, { ...c, pos: at });
  return liveAuras(state).filter((aura) => auraSubject(aura, c) && walkEnters(walk, (at) => inAt(aura, at)));
}

/**
 * The first drone (id order) whose live aura carries `facet` and holds `c` on the facet's side:
 * the drone's own side for `ally_soft_cover`, the other side for `deny_invisible` and `deny_hide`.
 * `undefined` when none does.
 */
export function auraFacetSource(state: EncounterState, c: Combatant, facet: AuraFacet): Combatant | undefined {
  const allied = facet === "ally_soft_cover";
  for (const aura of liveAuras(state)) {
    if (!aura.profile.facets?.includes(facet)) continue;
    if ((sideOf(aura.drone) === sideOf(c)) !== allied) continue;
    if (inAura(state, aura, c)) return aura.drone;
  }
  return undefined;
}
